import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { motion } from 'framer-motion';
import SectionHeader from '@/components/shared/SectionHeader';
import { ArrowRight, MapPin } from 'lucide-react';

const ProjectCard = ({ p }) => (
    <Card
        className="h-full overflow-hidden bg-card border-border shadow-[0_10px_30px_rgba(15,23,42,0.05)] card-lift"
        data-testid="project-card"
    >
        <div className="aspect-[16/10] overflow-hidden bg-muted">
            <img
                src={p.image_url}
                alt={p.title}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
            />
        </div>
        <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
                <Badge
                    className={
                        p.status === 'ongoing'
                            ? 'bg-[hsl(var(--accent))]/15 text-[hsl(30_90%_28%)] border-[hsl(var(--accent))]/30'
                            : 'bg-[hsl(142_45%_92%)] text-[hsl(142_45%_26%)] border-[hsl(142_35%_78%)]'
                    }
                >
                    {p.status === 'ongoing' ? 'Ongoing' : 'Completed'}
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                    {p.category}
                </Badge>
            </div>
            <div className="font-display font-semibold text-lg leading-snug">{p.title}</div>
            <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {p.location}
            </div>
            <p className="mt-3 text-sm text-muted-foreground line-clamp-2">{p.summary}</p>
        </CardContent>
    </Card>
);

const ProjectsSection = ({ projects = [] }) => {
    const ongoing = projects.filter((p) => p.status === 'ongoing');
    const completed = projects.filter((p) => p.status === 'completed');
    // Cap the visible cards per tab so the section stays balanced regardless of DB size.
    const MAX_PER_TAB = 6;
    const ongoingVisible = ongoing.slice(0, MAX_PER_TAB);
    const completedVisible = completed.slice(0, MAX_PER_TAB);

    return (
        <section className="section-pad" data-testid="home-projects-section">
            <div className="container-page">
                <div className="flex items-end justify-between gap-6 flex-wrap">
                    <SectionHeader
                        eyebrow="Projects"
                        title="Ongoing & completed initiatives."
                        description="Impact you can see — across employment, health, environment and disaster relief."
                    />
                    <Link
                        to="/csr/our-projects"
                        className="text-sm font-semibold text-[hsl(var(--primary))] flex items-center gap-1 hover:underline"
                        data-testid="projects-view-all"
                    >
                        Explore all projects <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>

                <Tabs defaultValue="ongoing" className="mt-8" data-testid="projects-tabs">
                    <TabsList className="bg-[hsl(var(--secondary))]">
                        <TabsTrigger value="ongoing" data-testid="projects-tab-ongoing">
                            Ongoing ({ongoing.length})
                        </TabsTrigger>
                        <TabsTrigger value="completed" data-testid="projects-tab-completed">
                            Completed ({completed.length})
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="ongoing" className="mt-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {ongoingVisible.map((p, i) => (
                                <motion.div
                                    key={p.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: i * 0.05 }}
                                    viewport={{ once: true, margin: '-60px' }}
                                >
                                    <ProjectCard p={p} />
                                </motion.div>
                            ))}
                        </div>
                    </TabsContent>
                    <TabsContent value="completed" className="mt-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {completedVisible.map((p, i) => (
                                <motion.div
                                    key={p.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: i * 0.05 }}
                                    viewport={{ once: true, margin: '-60px' }}
                                >
                                    <ProjectCard p={p} />
                                </motion.div>
                            ))}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </section>
    );
};

export default ProjectsSection;
